import { useEffect, useId, useMemo } from "react";
import { fetchAddedBoard } from "@/features/settings/api/addedBoards";
import {
  type BoardAdditionEvent,
  type BoardAdditionState,
  createBoardAdditionMediator,
  isBoardAdditionEvent,
} from "@/features/settings/mediators/boardAdditionMediator";
import {
  type AddedBoard,
  canEnableMixBoard,
  MIX_MAX_BOARD_MESSAGE,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import { ApiError } from "@/shared/api/errors";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

type BoardAdditionProps = Readonly<{ defaultSlugs: readonly string[] }>;
type BoardAdditionViewProps = Readonly<{
  state: BoardAdditionState;
  addedBoards: readonly AddedBoard[];
  unavailableSlugs: readonly string[];
  onEvent: (event: BoardAdditionEvent) => void;
}>;

/** 追加・解除の入力をMediatorへ渡し、検証と永続Modelを接続する。 */
export function BoardAddition({
  defaultSlugs,
}: BoardAdditionProps): React.ReactElement {
  const addedBoards = useMixStore((state) => state.addedBoards);
  const unavailableSlugs = useMixStore((state) => state.unavailableSlugs);
  const connection = useMemo(() => {
    let controller: AbortController | null = null;
    const mediator = createBoardAdditionMediator(
      () => [
        ...defaultSlugs,
        ...useMixStore.getState().addedBoards.map((board) => board.slug),
      ],
      (effect) => {
        if (effect.type === "add") {
          useMixStore.getState().addBoard(effect.board);
          return;
        }
        if (effect.type === "remove") {
          controller?.abort();
          useMixStore.getState().removeBoard(effect.slug);
          return;
        }
        controller?.abort();
        controller = new AbortController();
        const signal = controller.signal;
        const revision = useMixStore.getState().boardRevision;
        void (async (): Promise<void> => {
          try {
            const board = await fetchAddedBoard(effect.slug, signal);
            signal.throwIfAborted();
            if (useMixStore.getState().boardRevision !== revision) {
              mediator.dispatch({
                type: "board-addition/failed",
                requestId: effect.requestId,
                message: "設定が変更されました。再度追加してください",
              });
              return;
            }
            mediator.dispatch({
              type: "board-addition/completed",
              requestId: effect.requestId,
              board,
            });
          } catch (error) {
            if (signal.aborted) return;
            mediator.dispatch({
              type: "board-addition/failed",
              requestId: effect.requestId,
              message:
                error instanceof ApiError && error.status === 404
                  ? "この板slugは登録されていません"
                  : "板情報を取得できませんでした。時間をおいて再度お試しください",
            });
          }
        })();
      },
      (slug) =>
        canEnableMixBoard(useMixStore.getState(), slug)
          ? ""
          : MIX_MAX_BOARD_MESSAGE,
    );
    return { mediator, abort: (): void => controller?.abort() };
  }, [defaultSlugs]);
  useHierarchyLifetime(connection.mediator);
  useEffect(() => connection.abort, [connection]);
  const state = useMediatorSnapshot(connection.mediator);
  const node = useComponentNode((event) =>
    isBoardAdditionEvent(event)
      ? connection.mediator.dispatch(event).status === "handled"
        ? "handled"
        : "unhandled"
      : "unhandled",
  );
  return (
    <ComponentScope node={node}>
      <BoardAdditionView
        state={state}
        addedBoards={addedBoards}
        unavailableSlugs={unavailableSlugs}
        onEvent={(event): void => {
          node.dispatch(event);
        }}
      />
    </ComponentScope>
  );
}

/** 板slugの明示入力と追加済み板の解除。Viewは保存・APIを操作しない。 */
export function BoardAdditionView({
  state,
  addedBoards,
  unavailableSlugs,
  onEvent,
}: BoardAdditionViewProps): React.ReactElement {
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  return (
    <section className="px-4 py-3">
      <form
        onSubmit={(event): void => {
          event.preventDefault();
          onEvent({ type: "board-addition/add-requested" });
        }}
      >
        <label
          htmlFor={inputId}
          className="block text-sm font-bold text-foreground"
        >
          板slugを追加
        </label>
        <p id={hintId} className="mt-1 text-xs text-muted-foreground">
          登録済みの板slugを入力すると、このブラウザーの候補へ追加します。
        </p>
        <fieldset
          className="mt-2 flex min-w-0 flex-wrap gap-2 border-0 p-0"
          disabled={state.pending}
        >
          <legend className="sr-only">追加する板</legend>
          <input
            id={inputId}
            type="text"
            autoCapitalize="none"
            autoComplete="off"
            spellCheck={false}
            value={state.draft}
            aria-invalid={Boolean(state.error)}
            aria-describedby={`${hintId} ${errorId}`}
            className="h-11 min-w-0 flex-1 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            onChange={(event): void =>
              onEvent({
                type: "board-addition/draft-changed",
                value: event.currentTarget.value,
              })
            }
          />
          <button
            type="submit"
            className="h-11 rounded-lg bg-primary px-4 text-sm font-bold text-primary-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {state.pending ? "確認中…" : "追加"}
          </button>
        </fieldset>
        <p id={errorId} role="alert" className="mt-1 text-sm text-destructive">
          {state.error}
        </p>
        <p role="status" className="mt-1 text-sm text-muted-foreground">
          {state.message}
        </p>
      </form>
      {addedBoards.length > 0 && (
        <ul className="mt-2 list-none space-y-2 p-0">
          {addedBoards.map((board) => (
            <li
              key={board.slug}
              className="flex flex-wrap items-center justify-between gap-2 text-sm"
            >
              <p>
                {board.boardName}{" "}
                <small className="text-muted-foreground">
                  ({board.slug})
                  {unavailableSlugs.includes(board.slug)
                    ? " — 現在は未登録のため利用できません"
                    : ""}
                </small>
              </p>
              <button
                type="button"
                className="min-h-11 rounded-lg border border-border px-3 text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                onClick={(): void =>
                  onEvent({
                    type: "board-addition/remove-requested",
                    slug: board.slug,
                  })
                }
              >
                追加解除 <small className="sr-only">{board.boardName}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
